// Utility to generate Gmail filter XML export file
class FilterXmlExport {
  constructor() {
    this.labelName = 'Not Selected';
  }

  // Generate a unique filter ID based on timestamp
  generateFilterId() {
    return Date.now().toString();
  }

  // Get current timestamp in ISO format
  getCurrentTimestamp() {
    return new Date().toISOString();
  }

  // Escape XML special characters
  escapeXml(text) {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  // Create a single filter entry
  createFilterEntry(senderEmail, filterId, timestamp) {
    return `  <entry>
    <category term="filter"/>
    <title>Mail Filter</title>
    <id>tag:mail.google.com,2008:filter:${filterId}</id>
    <updated>${timestamp}</updated>
    <content/>
    <apps:property name="from" value="${this.escapeXml(senderEmail)}"/>
    <apps:property name="label" value="${this.escapeXml(this.labelName)}"/>
    <apps:property name="shouldArchive" value="true"/>
  </entry>`;
  }

  // Create a catch-all filter that excludes selected senders
  createInverseFilterEntry(selectedEmails, filterId, timestamp) {
    // Build query: list all selected sender emails to EXCLUDE from this filter
    const senderQuery = selectedEmails
      .map(email => `from:(${this.escapeXml(email)})`)
      .join(' OR ');

    return `  <entry>
    <category term="filter"/>
    <title>Mail Filter - Not Selected</title>
    <id>tag:mail.google.com,2008:filter:${filterId}</id>
    <updated>${timestamp}</updated>
    <content/>
    <apps:property name="doesNotHaveTheWord" value="${senderQuery}"/>
    <apps:property name="label" value="${this.escapeXml(this.labelName)}"/>
    <apps:property name="shouldArchive" value="true"/>
  </entry>`;
  }

  // Generate complete XML document with all filters
  generateXml(senderEmails, userEmail = 'user@gmail.com', userName = 'Gmail User') {
    const timestamp = this.getCurrentTimestamp();
    const feedId = this.generateFilterId();

    // Create filter entries
    const filterEntries = senderEmails.map(email => {
      const filterId = this.generateFilterId();
      return this.createFilterEntry(email, filterId, timestamp);
    }).join('\n');

    // Build complete XML document
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<feed xmlns="http://www.w3.org/2005/Atom" xmlns:apps="http://schemas.google.com/apps/2006">
  <title>Mail Filters</title>
  <id>tag:mail.google.com,2008:filters:${feedId}</id>
  <updated>${timestamp}</updated>
  <author>
    <name>${this.escapeXml(userName)}</name>
    <email>${this.escapeXml(userEmail)}</email>
  </author>
${filterEntries}
</feed>`;

    return xml;
  }

  // Generate inverse filter XML (keep selected in inbox, archive everything else)
  generateInverseXml(selectedEmails, userEmail = 'user@gmail.com', userName = 'Gmail User') {
    const timestamp = this.getCurrentTimestamp();
    const feedId = this.generateFilterId();

    // Create single catch-all filter that excludes selected senders
    const filterId = this.generateFilterId();
    const filterEntry = this.createInverseFilterEntry(selectedEmails, filterId, timestamp);

    // Build complete XML document
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<feed xmlns="http://www.w3.org/2005/Atom" xmlns:apps="http://schemas.google.com/apps/2006">
  <title>Mail Filters</title>
  <id>tag:mail.google.com,2008:filters:${feedId}</id>
  <updated>${timestamp}</updated>
  <author>
    <name>${this.escapeXml(userName)}</name>
    <email>${this.escapeXml(userEmail)}</email>
  </author>
${filterEntry}
</feed>`;

    return xml;
  }

  // Download XML file
  downloadXml(senderEmails, userEmail, userName) {
    const xml = this.generateXml(senderEmails, userEmail, userName);
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'gmail-filters.xml';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Download inverse filter XML file
  downloadInverseXml(selectedEmails, userEmail, userName) {
    const xml = this.generateInverseXml(selectedEmails, userEmail, userName);
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'gmail-filters-inverse.xml';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export default new FilterXmlExport();
